import { useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ExpandedTable, IButton, ISelect, Layout, Spinner } from "../../ui-kit";
import { Controller } from "react-hook-form";
import { useHandleForm } from "../../hooks/useHandleForm";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import classNames from "classnames";
import * as yup from "yup";
import _ from "lodash";
import apiAdmin from "../Admin/api";
import RegistryApi from "../../api/Registry";
import Paths from "../../constants/PathsBreadcrumbs";
import { FilterCondition } from "../../constants/Enums";
import styles from "./RsoSettings.module.scss";
import { findCurrentObj } from "../../utils/helpers/object.helpers";

const validationSchema = yup.object().shape({
  items: yup.array().of(
    yup.object().shape({
      organizationId: yup
        .number()
        .nullable(true)
        .required("Поле обязательно для заполнения"),
    }),
  ),
});

const defaultColumns = {
  id: "ID РСО",
  title: "Название организации",
  organizationId: "ID Учетной записи организации",
};
interface IRsoSettings {
  urls?: { get: string; put: string };
  columnsTitle?: { [key: string]: string };
}

export default function RsoSettings({
  urls,
  columnsTitle = defaultColumns,
}: IRsoSettings) {
  const {
    isLoading: isRsoOrgsLoading,
    isFetching: isRsoOrgsFetching,
    data: rsoOrganizations,
    refetch: refetchRsoOrganizations,
  } = useQuery({
    queryKey: ["rsoOrganizations"],
    queryFn: () =>
      RegistryApi.getRsoOrganizations(urls?.get)
        .then((res) => res?.data)
        .catch((e) => console.log(e)),
  });

  const { isLoading: isAllOrgsLoading, data: allOrganizations } = useQuery({
    queryKey: ["allOrganizations"],
    queryFn: () =>
      apiAdmin
        .getAllOrganizations(0, 0, "", [
          {
            fieldName: "isRso",
            value: true,
            condition: FilterCondition.Equal,
          },
        ])
        .then(({ data }) => data?.items || [])
        .catch((e) => console.log(e)),
  });

  const { isLoading: isSaving, mutate: updateRsoOrganizations } = useMutation<
    unknown,
    unknown,
    { items: any[] }
  >({
    mutationFn: (payload) =>
      RegistryApi.updateRsoOrganizations(payload, urls?.put),
    onSuccess: refetchRsoOrganizations,
  });

  useBreadcrumbs(Paths.spRsoSettings);

  const onSubmit = (values) => {
    values.items.forEach((organization) => {
      if (!organization.organizationName) {
        const value =
          findCurrentObj(organization?.organizationId, allOrganizations)
            ?.name || "";
        organization.organizationName = value;
      }
    });
    updateRsoOrganizations(values);
  };

  const { control, reset, handleSubmit, formState } = useHandleForm(
    validationSchema,
    { items: [] },
  );

  useEffect(() => {
    if (!isRsoOrgsLoading && !isRsoOrgsFetching) {
      reset({ items: rsoOrganizations });
    }
  }, [isRsoOrgsLoading, isRsoOrgsFetching]);

  const columns = [
    {
      title: columnsTitle.id,
      dataIndex: "id",
      key: "1",
      render: (text) => text,
    },
    {
      title: columnsTitle.title,
      dataIndex: "title",
      key: "2",
      render: (text) => text,
    },
    {
      title: columnsTitle.organizationId,
      dataIndex: "organizationId",
      key: "3",
      render: (_text, val, idx) => {
        return (
          <Controller
            control={control}
            name={`items[${idx}].organizationId`}
            render={({ field, fieldState: { error } }) => (
              <ISelect
                field={field}
                optionsArr={allOrganizations}
                placeholder="Учетная запись организации"
                error={error?.message}
                isRequired
              />
            )}
          />
        );
      },
    },
  ];

  return (
    <Layout type="white">
      {isRsoOrgsLoading || isAllOrgsLoading || isSaving ? (
        <Spinner />
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={classNames(styles.form)}
        >
          <ExpandedTable
            columns={columns}
            data={rsoOrganizations}
            title="Настройка РСО"
          />
          <IButton
            type="submit"
            className={styles.button}
            disabled={!formState.isDirty}
          >
            Сохранить
          </IButton>
        </form>
      )}
    </Layout>
  );
}
